import { useEffect, useRef } from "react";
import { trackBlogEvent } from "@/lib/blogApi";

const VISITOR_KEY = "spiruva_visitor_id";

const getVisitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      // Lightweight UUID v4 (no crypto.randomUUID dep)
      id = "v_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return `anon_${Date.now()}`;
  }
};

/**
 * Reports a `view` on mount and a `complete` event when the user has scrolled
 * past the article. The completion % is the max ratio of (scrolledOver / articleHeight)
 * seen during the visit (saturated at 1.0). Sent on visibility-hidden + on unmount
 * to capture short sessions.
 */
export const useBlogTracker = (slug, articleRef) => {
  const visitorId = useRef(getVisitorId());
  const maxCompletion = useRef(0);
  const viewSent = useRef(false);
  const completeSent = useRef(false);

  // Fire the view event once per slug visit.
  useEffect(() => {
    if (!slug || viewSent.current) return;
    viewSent.current = true;
    trackBlogEvent({
      slug,
      visitor_id: visitorId.current,
      referrer: document.referrer || null,
      event: "view",
    });
  }, [slug]);

  // Track scroll completion + fire on hide/unload.
  useEffect(() => {
    if (!slug) return;
    const measure = () => {
      const el = articleRef?.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight || 0;
      const articleH = el.offsetHeight || 1;
      // Pixels of the article currently above the bottom of the viewport.
      const scrolledOver = Math.max(0, viewportH - rect.top);
      const ratio = Math.min(1, scrolledOver / articleH);
      if (ratio > maxCompletion.current) maxCompletion.current = ratio;
    };
    const flushComplete = () => {
      if (completeSent.current) return;
      if (maxCompletion.current <= 0) return;
      completeSent.current = true;
      trackBlogEvent({
        slug,
        visitor_id: visitorId.current,
        completion: Number(maxCompletion.current.toFixed(3)),
        event: "complete",
      });
    };
    const onVis = () => { if (document.visibilityState === "hidden") flushComplete(); };
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", flushComplete);
    // Initial measurement
    measure();
    return () => {
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", flushComplete);
      flushComplete();
    };
  }, [slug, articleRef]);
};
